import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { session } from '../lib/api';
import { subscribe, flush, type OutboxState } from '../lib/outbox';

/** Honest, always-visible sync state. Staff must never have to guess. */
function SyncBadge() {
  const [s, setS] = useState<OutboxState>({ pending: 0, online: true, flushing: false });
  useEffect(() => subscribe(setS), []);

  const cls = !s.online
    ? 'bg-amber-900/60 text-amber-200 border-amber-700'
    : s.pending
      ? 'bg-sky-900/60 text-sky-200 border-sky-700'
      : 'bg-emerald-900/50 text-emerald-200 border-emerald-800';

  const text = !s.online
    ? `Offline${s.pending ? ` · ${s.pending} queued` : ''}`
    : s.flushing
      ? 'Syncing…'
      : s.pending
        ? `${s.pending} queued`
        : 'Online · synced';

  return (
    <button
      onClick={() => void flush()}
      title={s.lastError ?? 'Click to sync now'}
      className={`badge border ${cls} gap-1.5 cursor-pointer`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${!s.online ? 'bg-amber-400' : s.pending ? 'bg-sky-400' : 'bg-emerald-400'}`} />
      {text}
    </button>
  );
}

export default function Shell() {
  const nav = useNavigate();
  const s = session.get()!;

  const link = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-1.5 rounded-lg text-sm font-medium ${
      isActive ? 'bg-brand text-white' : 'text-gray-300 hover:bg-gray-800'
    }`;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-gray-900 border-b border-gray-800 px-4 py-2 flex items-center gap-3 shrink-0">
        <div className="font-bold tracking-tight text-brand text-lg">FEMI9</div>
        <span className="text-xs text-gray-500">POS</span>

        <nav className="flex gap-1 ml-4">
          <NavLink to="/" end className={link}>Till</NavLink>
          <NavLink to="/returns" className={link}>Returns</NavLink>
          <NavLink to="/shift" className={link}>Shift</NavLink>
          {(s.user.scopes.includes('report.store') || s.user.role === 'admin') && (
            <NavLink to="/admin" className={link}>Back office</NavLink>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          <SyncBadge />
          <div className="text-right leading-tight">
            <div className="text-sm font-medium">{s.user.name}</div>
            <div className="text-[11px] text-gray-500">
              {s.storeCode} · Till {s.terminalCode} · {s.user.role.replace('_', ' ')}
            </div>
          </div>
          <button
            className="btn-ghost text-sm py-1.5"
            onClick={() => { session.clear(); nav('/login'); }}
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="flex-1 min-h-0">
        <Outlet />
      </main>
    </div>
  );
}
